import { bootstrapSheet } from '../../core/styles/bootstrap.js';
import { eumBaseSheet } from '../../core/styles/eum-base.js';
import { icon, IconName } from '../../core/icons/icons.js';
import { i18n } from '../../core/i18n/i18n-service.js';
// @ts-ignore — handled by esbuild inline-css plugin
import componentSheet from './eum-view-switcher.css';

type ViewKey = 'cards' | 'grid' | 'table';

const VIEW_ICONS: Record<ViewKey, { titleKey: string; icon: IconName }> = {
    cards: { titleKey: 'viewSwitcher.cards', icon: 'person-vcard' },
    grid: { titleKey: 'viewSwitcher.grid', icon: 'grid' },
    table: { titleKey: 'viewSwitcher.table', icon: 'table' },
};

/**
 * <eum-view-switcher> — cards / grid / table toggle.
 * Driven via attributes:
 *  - views:   comma-separated subset of 'cards,grid,table' (default: all three).
 *             The legacy `columns` alias maps to `grid`.
 *  - current: the active view key.
 * Fires `eum-view-change` with { view }.
 */
export class EumViewSwitcher extends HTMLElement {
    private container!: HTMLElement;

    static get observedAttributes(): string[] {
        return ['views', 'current'];
    }

    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: 'open' });
            shadow.adoptedStyleSheets = [bootstrapSheet, eumBaseSheet, componentSheet];
            shadow.innerHTML = /*html*/ `<div class="views" role="group"></div>`;
            this.container = shadow.querySelector('.views')!;

            this.container.addEventListener('click', (e: Event) => {
                const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-view]');
                if (!btn) return;
                const view = btn.dataset.view as ViewKey;
                if (view === this.current) return;
                this.dispatchEvent(
                    new CustomEvent('eum-view-change', {
                        bubbles: true,
                        composed: true,
                        detail: { view },
                    })
                );
            });
        }
        this.render();
    }

    attributeChangedCallback(): void {
        if (this.container) this.render();
    }

    private get views(): ViewKey[] {
        const raw = this.getAttribute('views');
        const all: ViewKey[] = ['cards', 'grid', 'table'];
        if (!raw) return all;
        const requested = raw
            .split(',')
            .map((v) => v.trim().toLowerCase())
            .map((v) => (v === 'columns' ? 'grid' : v))
            .filter((v): v is ViewKey => (all as string[]).includes(v));
        return requested.length > 0 ? requested : all;
    }

    private get current(): string {
        return this.getAttribute('current') || 'cards';
    }

    private render(): void {
        const current = this.current;
        this.container.innerHTML = this.views
            .map((view) => {
                const def = VIEW_ICONS[view];
                const title = this.escapeAttr(i18n.t(def.titleKey));
                return /*html*/ `
                    <button type="button" class="icon-btn btn${view === current ? ' active' : ''}"
                        data-view="${view}" title="${title}" aria-label="${title}"
                        aria-pressed="${view === current}">
                        ${icon(def.icon)}
                    </button>`; 
            })
            .join('');
    }

    private escapeAttr(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    }
}

customElements.define('eum-view-switcher', EumViewSwitcher);
